// Mapping from a stable color "key" → Tailwind class names.
// Listing full classnames here ensures Tailwind's content scan keeps them.
export const COLOR_PALETTE = {
  emerald: { dot: "bg-emerald-500", bg: "bg-emerald-100", text: "text-emerald-800", swatch: "bg-emerald-500", ring: "ring-emerald-300" },
  blue:    { dot: "bg-blue-500",    bg: "bg-blue-100",    text: "text-blue-800",    swatch: "bg-blue-500",    ring: "ring-blue-300" },
  violet:  { dot: "bg-violet-500",  bg: "bg-violet-100",  text: "text-violet-800",  swatch: "bg-violet-500",  ring: "ring-violet-300" },
  orange:  { dot: "bg-orange-500",  bg: "bg-orange-100",  text: "text-orange-900",  swatch: "bg-orange-500",  ring: "ring-orange-300" },
  rose:    { dot: "bg-rose-500",    bg: "bg-rose-100",    text: "text-rose-800",    swatch: "bg-rose-500",    ring: "ring-rose-300" },
  amber:   { dot: "bg-amber-500",   bg: "bg-amber-100",   text: "text-amber-900",   swatch: "bg-amber-500",   ring: "ring-amber-300" },
  teal:    { dot: "bg-teal-500",    bg: "bg-teal-100",    text: "text-teal-800",    swatch: "bg-teal-500",    ring: "ring-teal-300" },
  slate:   { dot: "bg-slate-500",   bg: "bg-slate-100",   text: "text-slate-800",   swatch: "bg-slate-500",   ring: "ring-slate-300" },
};

export const COLOR_KEYS = Object.keys(COLOR_PALETTE);

// Each named token returns Tailwind class strings; a hex code returns
// inline-style props instead. Consumers always render with both:
//   <span className={cn("...", color.dot)} style={color.dotStyle} />
// For named tokens, *Style props are undefined (no-op).
const HEX_RE = /^#?([0-9a-fA-F]{6})$/;

const NAMED_EMPTY_STYLES = {
  dotStyle: undefined,
  badgeStyle: undefined,
  swatchStyle: undefined,
  ringStyle: undefined,
};

export function isHexColor(value) {
  return typeof value === "string" && HEX_RE.test(value.trim());
}

function normalizeHex(value) {
  const m = String(value || "").trim().match(HEX_RE);
  return m ? `#${m[1].toLowerCase()}` : null;
}

// Relative luminance per WCAG (0..1). Used to flip text color on light hexes.
function luminance(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const ch = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

function hexAlpha(hex, alpha) {
  // alpha 0..1 → two-digit hex
  const a = Math.max(0, Math.min(255, Math.round(alpha * 255)))
    .toString(16)
    .padStart(2, "0");
  return `${hex}${a}`;
}

export function getColor(value) {
  if (value && typeof value === "string") {
    const lower = value.toLowerCase();
    if (COLOR_PALETTE[lower]) {
      return { ...COLOR_PALETTE[lower], ...NAMED_EMPTY_STYLES, isHex: false, hex: null };
    }
    const hex = normalizeHex(value);
    if (hex) {
      const lum = luminance(hex);
      // Pick a readable badge text: if the chosen hue is very light, use brand ink.
      const badgeTextColor = lum > 0.62 ? "#0F1A17" : hex;
      return {
        // Empty class strings so cn() ignores them — the inline styles drive rendering.
        dot: "",
        bg: "",
        text: "",
        swatch: "",
        ring: "",
        isHex: true,
        hex,
        dotStyle: { backgroundColor: hex },
        // Soft tinted badge (~20% alpha) with bold hex text
        badgeStyle: { backgroundColor: hexAlpha(hex, 0.2), color: badgeTextColor },
        swatchStyle: { backgroundColor: hex },
        ringStyle: { boxShadow: `0 0 0 2px ${hexAlpha(hex, 0.4)}` },
      };
    }
  }
  return { ...COLOR_PALETTE.slate, ...NAMED_EMPTY_STYLES, isHex: false, hex: null };
}
